import { createCompositionElement } from '@src/services/composition/CompositionElementService';
import { resolveOverlayRevealTime } from '@src/services/composition/overlayRevealTime';
import { cardSlotElementIds } from '@src/services/videoTemplate/cardSlots';
import { cardSlotOf } from '@src/services/videoTemplate/uploadedMediaCard';
import {
  useDatoClient,
  useKaraokeStore,
  useLayoutStore,
  useStore,
  useTemplatedPrompts,
  useTranscription,
  useUserIdentity,
  useVideoSource,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { resolveAndApplySpeakerForCompositionElement } from '@src/utility/compositionSettings';
import { extractErrorInfo } from '@src/utility/error';
import { storeCompositionTemplateMetadata } from '@src/utility/extraElementData';
import { LogManager } from '@src/utility/Logger';
import { hasSpeakerData } from '@src/utility/speakerUtils';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useRef } from 'react';
import styled from 'styled-components';

import ToggleSidebarIcon from '../../svgs/ToggleSidebarIcon';
import {
  AIProducerCard,
  DesignCard,
  MediaCard,
  SidebarOption,
} from '../../types.ts/general';
import { PhotoArtifactTab } from '../../types.ts/story';
import { Breadcrumbs } from '../Breadcrumbs';
import AIClipProducer from '../ClipProducer/AIClipProducer';
import SpinningLoading from '../SpinningLoading';
import { VideoVersionHistory } from '../stage/VideoVersionHistory';
import AIFramingProducer from './AIFramingProducer';
import AIMusicProducer from './AIMusicProducer';
import AIPhotoPunchListProducer from './AIPhotoPunchListProducer';
import { useAiProducerCards } from './aiProducerCards';
import AISceneProducer from './AISceneProducer';
import ArtifactsAndAssets from './ArtifactsAndAssets';
import { AudioSettings } from './AudioSettings';
import ClipProducerSidebarUI from './ClipProducerSidebarUI';
import {
  CompositionProvider,
  useCompositionContext,
} from './CompositionContext';
import { CompositionSettings } from './CompositionSettings';
import CreatorStudioSpeakersPanel from './CreatorStudioSpeakersPanel';
import {
  getActiveElementSettings,
  isBasicTextElement,
} from './elementSettingsUtils';
import ExpandablePanel, {
  EXPANDED_PANEL_COLUMN_WIDTH,
} from './ExpandablePanel';
import FloatingToolPanel from './FloatingToolPanel';
import { ImageSettings } from './ImageSettings';
import AIKaraokeTextProducer from './KaraokeSettings/AIKaraokeTextProducer';
import { CaptionsLanguageHeaderToggle } from './KaraokeSettings/CaptionsLanguageHeaderToggle';
import KaraokeEditorView from './KaraokeSettings/KaraokeEditorView';
import MusicLibrary from './MusicSettings/MusicLibrary';
import { ShapeSettings } from './ShapeSettings';
import SidebarIconMenu, { RAIL_MIN_WIDTH } from './SidebarIconMenu';
import SidePanelTabs from './SidePanelTabs';
import { TextSettings } from './TextSettings';
import VideoArtifacts from './VideoArtifacts';
import { VideoSettings } from './VideoSettings';
import { VideoTemplates } from './VideoTemplateSettings/VideoTemplate';
import { removeElementsAndMetadata } from './VideoTemplateSettings/videoTemplateHelpers';
import {
  useVideoTemplatesContext,
  VideoTemplatesProvider,
} from './VideoTemplateSettings/VideoTemplatesContext';

import type { ElementState } from '../../renderer/ElementState';
import type { PunchListItem, Story } from '../../types.ts/story';
import type { CompositionTemplate } from './CompositionSettings/types';
import type { EditorStore } from '@src/editor';

type Props = {
  story: Story | undefined;
};

type PanelContentProps = {
  musicProducerLoading?: boolean;
  children: React.ReactNode;
  sidebarRef?: React.RefObject<HTMLDivElement>;
  id?: string;
  title?: string;
  // PLAT-9621: optional inline element rendered next to the title (e.g.
  // the captions panel's language toggle). Lays out between the title and
  // the close button so it sits in the same row as the panel header.
  titleAdornment?: React.ReactNode;
  // PLAT-9621: shrink the panel's top padding so the header row visually
  // aligns with the transcript-side toolbar (which has 0 top padding).
  // Used by the captions branch.
  compactHeader?: boolean;
  breadcrumbs?: React.ReactNode;
};

/**
 * Rail sections on the floating-card shell (PLAT-11087) — now every tab. The one
 * remaining `PanelContent` consumer is the `activeElement` fallback at the end of
 * `getPanelContent`, which `sidebarOptions` never routes to: `routeSidebarForElement`
 * maps every element type this fallback could render to karaoke, media or design,
 * so it only survives for an element type neither function recognises. Left on the
 * old shell deliberately rather than given an invented title.
 *
 * Keep this in step with the branches that return a `FloatingToolPanel` — the flag
 * drives the panel's box, the branch drives its chrome, and a mismatch would put a
 * card inside a full-height frame or vice versa.
 */
const FLOATING_SIDEBAR_OPTIONS: ReadonlySet<SidebarOption> = new Set([
  SidebarOption.aiProducer,
  SidebarOption.design,
  SidebarOption.history,
  SidebarOption.karaoke,
  SidebarOption.media,
  SidebarOption.speakers,
  SidebarOption.videoTemplate,
  SidebarOption.videoTemplates,
]);

const logger = LogManager.getLogger('component:RightSidePanel');

type MediaCardComponentProps = {
  mediaSubMenu: MediaCard;
};

type DesignCardComponentProps = {
  designSubMenu: DesignCard;
  activeElement: ElementState | null | undefined;
  editorStore: EditorStore;
  onAddComposition: (composition: CompositionTemplate) => void;
};

const PanelContent: React.FC<PanelContentProps> = observer(
  (props: PanelContentProps) => {
    const videoCreator = useVideoCreatorStore();
    return (
      <PanelContentContainer
        onKeyDown={e => e.stopPropagation()}
        id={props.id}
        compactHeader={props.compactHeader}
      >
        {/* Close button floats in the top-right corner rather than occupying
            its own header row. With the panel title now visually hidden (the
            right rail already labels the section), the header row collapses to
            zero height when empty, letting the tab bar / content rise to the
            top of the panel. */}
        <CollapsePanelButton
          onClick={() => videoCreator.collapseSidebar()}
          aria-label="Close sidebar"
        >
          <ToggleSidebarIcon />
          <CollapseTooltip>Close sidebar</CollapseTooltip>
        </CollapsePanelButton>
        <PanelHeader>
          {(props.title || props.titleAdornment) && (
            <PanelTitleGroup>
              {props.title && <PanelTitle>{props.title}</PanelTitle>}
              {props.titleAdornment}
            </PanelTitleGroup>
          )}
          {props.breadcrumbs}
        </PanelHeader>
        <SidePanelTabs />
        <Panel ref={props.sidebarRef}>
          {/* todo: move to music specific component or rewrite all AI tabs to use same loader */}
          {props.musicProducerLoading && (
            <SpinningLoading
              text={
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span>Finding music based on mood…</span>
                </div>
              }
            />
          )}
          {props.children}
        </Panel>
      </PanelContentContainer>
    );
  },
);

const MediaCardComponent: React.FC<MediaCardComponentProps> = ({
  mediaSubMenu,
}) => {
  switch (mediaSubMenu) {
    case MediaCard.photo:
      return <ArtifactsAndAssets />;
    case MediaCard.music:
      return <MusicLibrary />;
    case MediaCard.video:
      return <VideoArtifacts />;
    default:
      return <ArtifactsAndAssets />;
  }
};

const DesignCardComponent: React.FC<DesignCardComponentProps> = ({
  designSubMenu,
  activeElement,
  editorStore,
  onAddComposition,
}) => {
  switch (designSubMenu) {
    case DesignCard.videoTemplate:
      return <VideoTemplates />;
    case DesignCard.text:
      return (
        <TextSettings activeElement={activeElement} editorStore={editorStore} />
      );
    case DesignCard.shape:
      return (
        <ShapeSettings
          activeElement={activeElement}
          editorStore={editorStore}
        />
      );
    case DesignCard.composition:
      return (
        <CompositionSettings
          activeElement={activeElement}
          onAddComposition={onAddComposition}
        />
      );
  }
};

// Inner component that uses the composition context
const RightSidePanelInner: React.FC<Props> = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const layoutStore = useLayoutStore();
  const templatedPrompts = useTemplatedPrompts();
  const transcriptionStore = useTranscription();
  const karaokeStore = useKaraokeStore();
  const userIdentity = useUserIdentity();
  let activeElement = videoCreator.getActiveElement();
  const { sidebarOptions, stockMusic } = videoCreator;
  const { cards: aiProducerCards, activeCard: aiProducerCard } =
    useAiProducerCards();
  const isPhotoCardVisible = aiProducerCards.includes(AIProducerCard.photo);
  const { isSidebarExpanded } = layoutStore;
  const { editorStore } = useStore();
  const sidebarRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { setSelectedCategoryId, setSelectedSubcategoryId } =
    useCompositionContext();

  // Get feature flags
  const { enableTranscriptEditor } = useFlagsCombination(
    userIdentity.currentRole,
  );

  // Determine if speaker selection should be enabled
  const enableSpeakerSelection = React.useMemo(() => {
    if (!enableTranscriptEditor) return false;

    const transcriptElements =
      transcriptionStore.getFinalTranscriptionElements();
    return hasSpeakerData(transcriptElements);
  }, [enableTranscriptEditor, transcriptionStore]);

  // Handle toggle expand for the sidebar
  const handleToggleExpand = useCallback(() => {
    layoutStore.toggleSidebarExpanded();
  }, [layoutStore]);

  // Selecting a timeline element no longer auto-opens the panel (PLAT-10391):
  // single-click only routes the active tab so its rail icon turns custard,
  // and the panel is opened by double-clicking the element (handled in
  // VideoCreatorStore.setActiveElement) or clicking the highlighted rail icon.

  useEffect(() => {
    if (sidebarOptions === SidebarOption.editing && !activeElement) {
      videoCreator.collapseSidebar();
    }
  }, [sidebarOptions, activeElement]);

  // On a narrow window the open panel's column would leave the stage too
  // small for the canvas and its transport row, so the transcript gives up
  // width while the panel is open and gets it back on close. The row we sit
  // in is MainView (ErrorBoundary adds no DOM node). Re-fit while open, since
  // the window can be resized under an open panel.
  useEffect(() => {
    if (!isSidebarExpanded) {
      layoutStore.restoreTranscriptAfterSidebar();
      return;
    }
    const row = containerRef.current?.parentElement;
    if (!row) return;
    const fit = () => {
      if (row.clientWidth <= 0) return;
      layoutStore.fitTranscriptBesideSidebar(
        row.clientWidth - EXPANDED_PANEL_COLUMN_WIDTH - RAIL_MIN_WIDTH,
      );
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(row);
    return () => observer.disconnect();
  }, [isSidebarExpanded, layoutStore]);

  // Fullscreen unmounts this panel; don't leave the transcript pinned narrow.
  useEffect(
    () => () => layoutStore.restoreTranscriptAfterSidebar(),
    [layoutStore],
  );

  useEffect(() => {
    if (videoCreator.designSubMenu !== DesignCard.composition) {
      setSelectedCategoryId(null);
      setSelectedSubcategoryId(null);
    }
  }, [videoCreator.designSubMenu]);

  useEffect(() => {
    let e = videoCreator.openPhotoElementReplacementModal?.element;
    if (
      isPhotoCardVisible &&
      e &&
      videoCreator.punchListManager.punchListItems?.some(
        (p: PunchListItem) => p.id === e?.source?.id,
      )
    ) {
      videoCreator.aiProducerSubMenu = AIProducerCard.photo;
    }
  }, [sidebarOptions, activeElement, isPhotoCardVisible]);

  //todo rework. this is a hack to get the photo punch list loader to show up when adding punch list manually
  useEffect(() => {
    if (
      isPhotoCardVisible &&
      sidebarOptions === SidebarOption.aiProducer &&
      (videoCreator.punchListLoading ||
        videoCreator.addedPunchListItemId ||
        videoCreator.savingStockPhoto)
    ) {
      videoCreator.aiProducerSubMenu = AIProducerCard.photo;
      // setSelectedCard(AIProducerCard.photo);
    }
  }, [
    sidebarOptions,
    isPhotoCardVisible,
    videoCreator.punchListLoading,
    videoCreator.addedPunchListItemId,
  ]);

  const datoClientStore = useDatoClient();
  const videoSourceStore = useVideoSource();
  const { activeTemplate, activeTemplateSettings, replaceTemplateElement } =
    useVideoTemplatesContext();

  // PLAT-11781 — placement can drop the card away from the frame on the
  // canvas, which reads as "nothing happened". Select it and seek into it.
  const revealAddedElement = async (
    elementId: string,
    element: { time: number; durationSeconds: number },
  ) => {
    // UI selection & activation (previously inside legacy util)
    editorStore.selectElement(elementId);
    await videoCreator.setActiveElements(elementId);
    const revealTime = resolveOverlayRevealTime({
      time: element.time,
      durationSeconds: element.durationSeconds,
      isPlaying: videoCreator.isPlaying,
    });
    if (revealTime !== null) {
      // The add only marks the editor dirty; the renderer's source is
      // pushed on a 300ms trailing debounce. An END-placed card lands at
      // the old timeline end, so seeking before that push targets a time
      // the renderer's source does not reach and the playhead is clamped
      // short. Flush first so the renderer knows the card exists.
      await videoSourceStore.flushChanges();
      void videoCreator.setTime(revealTime, true).catch((error: unknown) => {
        logger.warn('Failed to seek to the new overlay', {
          elementId,
          revealTime,
          ...extractErrorInfo(error),
        });
      });
    }
  };

  const createCompositionElementHandler = async (
    template: CompositionTemplate,
  ) => {
    // PLAT-11732 — one intro and one end card at a time. With a video
    // template applied, its slot owns the card (Replace keeps the template's
    // settings in step); without one, the card already there comes off first.
    const slot = cardSlotOf(template);
    if (slot && activeTemplate && activeTemplateSettings) {
      const [cardId] = await replaceTemplateElement(slot, template.id);
      videoSourceStore.refreshSource();
      const card = cardId ? editorStore.editor.getElementById(cardId) : null;
      if (cardId && card) await revealAddedElement(cardId, card);
      return;
    }
    if (slot) {
      const existing = cardSlotElementIds(
        videoCreator.currentVideo?.extraElementData as
          | Record<string, unknown>
          | undefined,
        editorStore.editor.timeline.allElements.map(el => el.id),
        slot,
      );
      if (existing.length) {
        removeElementsAndMetadata(
          existing.map(id => ({ id, type: slot })),
          editorStore,
          videoCreator,
          () => {},
          true,
        );
      }
    }

    const result = await createCompositionElement({
      template,
      videoProps: {
        duration: videoCreator.duration,
        getCurrentVideo: () => videoCreator.currentVideo,
        aspectRatio: videoCreator.getCurrentAspectRatio(),
        time: videoCreator.time,
        organization: videoCreator.organization,
        story: videoCreator.story,
        convertToVh: (v: string) => videoCreator.textProducer.convertToVh(v),
      },
      editorStore,
      upsertExtraElementDataCb: (
        elementId: string,
        template: CompositionTemplate,
      ) => {
        storeCompositionTemplateMetadata(
          videoCreator.currentVideo,
          elementId,
          template,
        );
      },
    });
    if (result.ok) {
      const { element, elementId } = result.value;
      if (elementId) {
        await revealAddedElement(elementId, element);
        // Speaker resolution remains via legacy util until speaker service extraction
        await resolveAndApplySpeakerForCompositionElement({
          elementId,
          editorStore,
          datoClientStore,
          videoCreator,
          // Resolve at the overlay's own position, not the playhead — the seek
          // above already moved `videoCreator.time` into the overlay.
          timelineTime: element.time,
        });
      }
    }
  };

  const getAiProducerCard = () => {
    switch (aiProducerCard) {
      case AIProducerCard.clip:
        return templatedPrompts?.clipProducerPresets?.length ? (
          <AIClipProducer
            supportedPresets={templatedPrompts.clipProducerPresets}
            enableSpeakerSelection={enableSpeakerSelection}
            ClipProducerUI={props => <ClipProducerSidebarUI {...props} />}
          />
        ) : (
          <SpinningLoading text="Loading presets…" />
        );
      case AIProducerCard.photo:
        return (
          <AIPhotoPunchListProducer
            story={props.story}
            selectedCard={aiProducerCard}
          />
        );
      case AIProducerCard.scenes:
        return (
          <AISceneProducer
            story={props.story}
            onNavigateToFraming={
              aiProducerCards.includes(AIProducerCard.framing)
                ? () => {
                    videoCreator.aiProducerSubMenu = AIProducerCard.framing;
                  }
                : undefined
            }
          />
        );
      case AIProducerCard.framing:
        return <AIFramingProducer story={props.story} />;
      default:
        return (
          <AIMusicProducer
            story={props.story}
            music={stockMusic}
            selectedCard={aiProducerCard}
          />
        );
    }
  };

  // All six Media surfaces (the library, plus the five element-settings drill-ins)
  // share one floating card; only the header and the settings body differ. A
  // plain function, not a component — a component declared here would be a new
  // type every render and remount its subtree.
  const mediaPanel = (
    body: React.ReactNode,
    breadcrumbs?: React.ReactNode,
    id?: string,
  ) => (
    <FloatingToolPanel
      title="Media"
      breadcrumbs={breadcrumbs}
      onClose={() => videoCreator.collapseSidebar()}
      subheader={<SidePanelTabs />}
      bodyRef={sidebarRef}
      id={id}
    >
      {body}
    </FloatingToolPanel>
  );

  // Shared by the captions and AI-producer panels, both of which gated on this
  // same flag under the full-height shell. Carried over verbatim, including the
  // pre-existing wart that the copy is music-specific while the flag also covers
  // captions — see the TODO that used to sit on `PanelContent`.
  const musicProducerLoader = videoCreator.musicProducerLoading ? (
    <SpinningLoading
      positionTop="20px"
      text={
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span>Finding Music Based on Mood</span>
        </div>
      }
    />
  ) : null;

  // Get the content for the expandable panel based on current sidebar option
  const getPanelContent = () => {
    if (sidebarOptions === SidebarOption.karaoke) {
      // "Captions" is the panel heading (parallel to "Video Templates",
      // "Speakers", etc.). The language toggle rides alongside it as a
      // title adornment — see `CaptionsLanguageHeaderToggle`.
      return (
        <FloatingToolPanel
          title="Captions"
          titleAdornment={
            <CaptionsLanguageHeaderToggle editor={editorStore.editor} />
          }
          loading={musicProducerLoader}
          onClose={() => videoCreator.collapseSidebar()}
          subheader={<SidePanelTabs />}
          bodyRef={sidebarRef}
        >
          {karaokeStore.defaultKaraokeConfig ? (
            <KaraokeEditorView
              fallbackKaraokeConfig={karaokeStore.defaultKaraokeConfig}
              ref={sidebarRef}
              activeElement={activeElement}
              editorStore={editorStore}
            />
          ) : (
            <AIKaraokeTextProducer
              ref={sidebarRef}
              activeElement={activeElement}
              editorStore={editorStore}
            />
          )}
        </FloatingToolPanel>
      );
    }

    if (sidebarOptions === SidebarOption.aiProducer) {
      return (
        <FloatingToolPanel
          title="AI Producer"
          loading={musicProducerLoader}
          onClose={() => videoCreator.collapseSidebar()}
          subheader={<SidePanelTabs />}
          bodyRef={sidebarRef}
        >
          {getAiProducerCard()}
        </FloatingToolPanel>
      );
    }

    if (sidebarOptions === SidebarOption.media) {
      if (
        activeElement &&
        videoCreator.isOriginalVideoElement(activeElement.source)
      ) {
        return mediaPanel(
          <VideoSettings
            activeElement={activeElement}
            editorStore={editorStore}
          />,
          <Breadcrumbs
            items={[
              {
                label: 'Media',
                onClick: () => {
                  videoCreator.mediaSubMenu = MediaCard.photo;
                  videoCreator.setActiveElements();
                },
              },
              { label: 'Video' },
            ]}
          />,
        );
      }
      if (activeElement && videoCreator.isLogoElement(activeElement)) {
        return mediaPanel(
          <ImageSettings
            activeElement={activeElement}
            editorStore={editorStore}
          />,
          <Breadcrumbs
            items={[
              {
                label: 'Media',
                onClick: () => {
                  videoCreator.selectedPhotoAssets = {
                    ...videoCreator.selectedPhotoAssets,
                    tab: PhotoArtifactTab.logo,
                  };
                  videoCreator.mediaSubMenu = MediaCard.photo;
                  videoCreator.setActiveElements();
                },
              },
              { label: 'Logo' },
            ]}
          />,
        );
      }
      if (activeElement && videoCreator.isVideoElement(activeElement)) {
        return mediaPanel(
          <VideoSettings
            activeElement={activeElement}
            editorStore={editorStore}
          />,
          <Breadcrumbs
            items={[
              {
                label: 'Media',
                onClick: () => {
                  videoCreator.mediaSubMenu = MediaCard.video;
                  videoCreator.setActiveElements();
                },
              },
              { label: 'Video' },
            ]}
          />,
        );
      }
      if (activeElement && videoCreator.isRegularImageElement(activeElement)) {
        return mediaPanel(
          <ImageSettings
            activeElement={activeElement}
            editorStore={editorStore}
          />,
          <Breadcrumbs
            items={[
              {
                label: 'Media',
                onClick: () => {
                  videoCreator.mediaSubMenu = MediaCard.photo;
                  videoCreator.setActiveElements();
                },
              },
              { label: 'Photo' },
            ]}
          />,
        );
      }
      if (activeElement && activeElement.source.type === 'audio') {
        return mediaPanel(
          <AudioSettings activeElement={activeElement} />,
          <Breadcrumbs
            items={[
              {
                label: 'Media',
                onClick: () => {
                  videoCreator.mediaSubMenu = MediaCard.music;
                  videoCreator.setActiveElements();
                },
              },
              { label: 'Audio' },
            ]}
          />,
          'audio-settings-wrapper',
        );
      }
      return mediaPanel(
        <MediaCardComponent mediaSubMenu={videoCreator.mediaSubMenu} />,
      );
    }

    if (sidebarOptions === SidebarOption.videoTemplate) {
      return (
        <FloatingToolPanel
          title="Video Templates"
          onClose={() => videoCreator.collapseSidebar()}
          subheader={<SidePanelTabs />}
          bodyRef={sidebarRef}
        >
          <VideoTemplates />
        </FloatingToolPanel>
      );
    }

    if (sidebarOptions === SidebarOption.history) {
      return (
        <FloatingToolPanel
          title="History"
          onClose={() => videoCreator.collapseSidebar()}
          subheader={<SidePanelTabs />}
          bodyRef={sidebarRef}
        >
          <VideoVersionHistory sidebarRef={sidebarRef} />
        </FloatingToolPanel>
      );
    }

    if (sidebarOptions === SidebarOption.speakers) {
      return (
        <FloatingToolPanel
          title="Speakers"
          onClose={() => videoCreator.collapseSidebar()}
          subheader={<SidePanelTabs />}
          bodyRef={sidebarRef}
        >
          <CreatorStudioSpeakersPanel />
        </FloatingToolPanel>
      );
    }

    if (sidebarOptions === SidebarOption.videoTemplates) {
      return (
        <FloatingToolPanel
          title="Video Templates"
          onClose={() => videoCreator.collapseSidebar()}
          subheader={<SidePanelTabs />}
          bodyRef={sidebarRef}
        >
          <VideoTemplates />
        </FloatingToolPanel>
      );
    }

    if (sidebarOptions === SidebarOption.design) {
      return (
        <FloatingToolPanel
          title="Graphics"
          onClose={() => videoCreator.collapseSidebar()}
          subheader={<SidePanelTabs />}
          bodyRef={sidebarRef}
        >
          <DesignCardComponent
            designSubMenu={videoCreator.designSubMenu}
            activeElement={activeElement}
            editorStore={editorStore}
            onAddComposition={createCompositionElementHandler}
          />
        </FloatingToolPanel>
      );
    }

    if (activeElement) {
      const settingsComponent = getActiveElementSettings({
        activeElement,
        videoCreator,
        sidebarRef,
        editorStore,
        onAddComposition: createCompositionElementHandler,
      });

      if (settingsComponent) {
        const panelContentProps: PanelContentProps = {
          children: <ScrollableArea>{settingsComponent}</ScrollableArea>,
          // Add special props for specific element types
          ...(isBasicTextElement(activeElement) ||
          activeElement.source.type === 'shape' ||
          activeElement.source.type === 'composition'
            ? { sidebarRef }
            : {}),
          ...(activeElement.source.type === 'audio'
            ? { id: 'audio-settings-wrapper' }
            : {}),
        };

        return <PanelContent {...panelContentProps} />;
      }
    }
  };

  return (
    <SidebarContainer ref={containerRef}>
      <ExpandablePanel
        isExpanded={isSidebarExpanded}
        isFloating={FLOATING_SIDEBAR_OPTIONS.has(sidebarOptions)}
      >
        {getPanelContent()}
      </ExpandablePanel>
      <SidebarIconMenu
        isExpanded={isSidebarExpanded}
        onToggleExpand={handleToggleExpand}
      />
    </SidebarContainer>
  );
});

const Panel = styled.div`
  position: relative;
  flex: 1;
  border-radius: var(--radius);
  overflow-y: auto;
`;

const SidebarContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
`;

const PanelContentContainer = styled.div<{ compactHeader?: boolean }>`
  position: relative;
  padding: ${p => (p.compactHeader ? '6px 16px 15px' : '15px 16px')};
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: var(--background);
  box-sizing: border-box;
`;

const ScrollableArea = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  /* Reserve space for the absolutely-positioned close button (top-right) so an
     inline adornment/breadcrumb never slides underneath it. Collapses to zero
     height when empty (the title is visually hidden), letting the tab bar and
     content rise to the top of the panel. */
  padding-right: 44px;
  flex-shrink: 0;
`;

const CollapseTooltip = styled.div`
  position: absolute;
  right: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
  background: var(--popover);
  color: var(--popover-foreground);
  font-size: 12px;
  font-weight: 500;
  padding: 5px 10px;
  border-radius: 6px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  z-index: 1000003;
  border: 1px solid var(--border-subtle);
  box-shadow: var(--elevation-sm);
`;

const CollapsePanelButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  transition:
    background var(--transition-fast) ease,
    color var(--transition-fast) ease;

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }

  &:hover ${CollapseTooltip} {
    opacity: 1;
  }

  &:active {
    background-color: var(--gray-700);
    transform: scale(0.95);
  }
`;

// The right rail (SidebarIconMenu) already renders the active section's label
// in --accent, so a visible panel title duplicated that signal and cost a line
// of vertical space at the top of every routed panel. Keep the title in the
// accessibility tree as a visually-hidden heading (screen readers / landmark
// navigation still announce the section) but render nothing visible, letting
// the panel content move up into the reclaimed space. The PanelHeader row
// itself stays for the close-sidebar button and any adornments/breadcrumbs.
const PanelTitle = styled.h2`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

/**
 * Groups the panel title with its optional adornment (e.g. the captions
 * language toggle) so they pack to the left of `PanelHeader` instead of
 * being scattered by its `justify-content: space-between` distribution.
 * The collapse button on the right stays opposite this whole group.
 */
const PanelTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  min-width: 0;
`;

// Wrap the component with CompositionProvider
export const RightSidePanel: React.FC<Props> = observer((props: Props) => {
  const videoCreator = useVideoCreatorStore();
  const { editorStore } = useStore();
  return (
    <CompositionProvider>
      <VideoTemplatesProvider
        videoCreator={videoCreator}
        editorStore={editorStore}
      >
        <RightSidePanelInner {...props} />
      </VideoTemplatesProvider>
    </CompositionProvider>
  );
});
